import * as React from 'react';
import { Button } from 'react-bootstrap';
import ZoomContainer from './ZoomContainer';

interface IImage {
    imageId: number;
    previewReference: string;
    highResReference: string;
    zoomed: boolean;
    highResLoading: boolean;
    mousePosition: {
        xPercent: number;
        yPercent: number;
    };
}

interface IProps {
    getSelectedImagesToCompare: () => any;
    onHide: () => void;
}

interface IState {
    images: IImage[];
    offset: number;
    zoom: boolean;
}

const ESCAPE_KEY = 27;
const N_KEY = 78;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;

class ImageProcessingCompare extends React.Component<IProps, IState> {
    constructor(props: IProps, context: any) {
        super(props, context);

        this.state = {
            images: [],
            offset: 0,
            zoom: false
        };

        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.stepThroughImages = this.stepThroughImages.bind(this);
        this.toggleZoom = this.toggleZoom.bind(this);
        this.resetZoom = this.resetZoom.bind(this);
    }

    componentDidMount() {
        let images = this.props.getSelectedImagesToCompare();
        document.addEventListener('keydown', this.handleKeyDown);
        this.setState({ images } as IState);

        document.body.style.overflow = 'hidden';
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        document.body.style.overflow = 'visible';
    }

    handleKeyDown = (event: any) => {
        switch (event.keyCode) {
            case ESCAPE_KEY:
                this.props.onHide();
                break;
            case N_KEY:
                this.props.onHide();
                break;
            case LEFT_ARROW:
                this.stepThroughImages(-1);
                break;
            case RIGHT_ARROW:
                this.stepThroughImages(1);
                break;
            default:
                break;
        }
    };

    stepThroughImages(value: number) {
        if (
            this.canStepRight(value, this.state.offset, this.state.images.length) ||
            this.canStepLeft(value, this.state.offset)
        ) {
            this.resetZoom();
            this.setState({ offset: this.state.offset + value } as IState);
        }
    }

    canStepRight(value: number, offset: number, length: number) {
        return value > 0 && offset < length - 4;
    }

    canStepLeft(value: number, offset: number) {
        return value < 0 && offset > 0;
    }

    toggleZoom(e: any, imageIndex: number) {
        let imageHeight = e.currentTarget.offsetHeight;
        let imageWidth = e.currentTarget.offsetWidth;
        let xPercent = e.nativeEvent.offsetX / imageWidth;
        let yPercent = e.nativeEvent.offsetY / imageHeight;

        let images = [...this.state.images];
        if (!images[imageIndex].zoomed) {
            images[imageIndex].highResLoading = true;
            images[imageIndex].mousePosition = { xPercent, yPercent };
        }
        images[imageIndex].zoomed = !images[imageIndex].zoomed;
        this.setState({ images } as IState);
    }

    resetZoom() {
        let images = this.state.images;
        images.forEach((image) => (image.zoomed = false));
        this.setState({ images } as IState);
    }

    setHighResImageLoaded(index: number) {
        let images = this.state.images;
        images[index].highResLoading = false;
        this.setState({ images } as IState);
    }

    render() {
        let maxNumberOfImages = this.state.images.length > 4 ? 4 : this.state.images.length;
        const { apiUrl } = window.appConfig;

        return (
            <div id="order-image-compare">
                <div className="zoomable-image fullscreen-view" id="zoomable-image">
                    <div className="floating-img-control">
                        <Button bsStyle="link" onClick={this.props.onHide}>
                            <i className="material-icons">fullscreen_exit</i>
                        </Button>
                    </div>
                    {[...Array(maxNumberOfImages)].map((_, i) => {
                        return (
                            <div
                                key={`compare-image-${i}`}
                                className={`compare-container ${
                                    this.state.images.length > 2 ? 'four-images' : 'two-images'
                                }`}
                            >
                                {(!this.state.images[i + this.state.offset].zoomed ||
                                    this.state.images[i + this.state.offset].highResLoading) && (
                                    <div className="image-container">
                                        <img
                                            src={`${apiUrl}/file/${
                                                this.state.images[i + this.state.offset]
                                                    .previewReference
                                            }`}
                                            onDoubleClick={(e) =>
                                                this.toggleZoom(e, i + this.state.offset)
                                            }
                                        />
                                        {this.state.images[i + this.state.offset]
                                            .highResLoading && (
                                            <span className="spin-container">
                                                <i className="load-spin-dark"></i>
                                            </span>
                                        )}
                                    </div>
                                )}
                                {this.state.images[i + this.state.offset].zoomed && (
                                    <ZoomContainer
                                        imageSrc={`${apiUrl}/file/${
                                            this.state.images[i + this.state.offset]
                                                .highResReference
                                        }`}
                                        loading={
                                            this.state.images[i + this.state.offset].highResLoading
                                        }
                                        toggleZoom={(e) =>
                                            this.toggleZoom(e, i + this.state.offset)
                                        }
                                        multipleZoomKey={`${i}`}
                                        onImageLoaded={() =>
                                            this.setHighResImageLoaded(i + this.state.offset)
                                        }
                                        xPercent={
                                            this.state.images[i + this.state.offset].mousePosition
                                                .xPercent
                                        }
                                        yPercent={
                                            this.state.images[i + this.state.offset].mousePosition
                                                .yPercent
                                        }
                                        comparing={true}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default ImageProcessingCompare;
